/**
 * PublicWatchPage — unauthenticated /watch/:video_id route.
 *
 * Serves a lightweight page anyone with the link can view: the walkthrough
 * video, its title/description, and a subtle CTA back to the landing page.
 * Only published videos are exposed via the backend public endpoint.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicWatchPage() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/public/walkthrough-videos/${videoId}`, { cache: 'no-store' })
      .then((r) => {
        if (r.status === 404) throw new Error('not_found');
        if (!r.ok) throw new Error('failed');
        return r.json();
      })
      .then((data) => { if (!cancelled) setVideo(data); })
      .catch((e) => { if (!cancelled) setNotFound(e.message === 'not_found'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [videoId]);

  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col" data-testid="public-watch-page">
      <header className="border-b border-slate-800 px-6 py-4">
        <button
          onClick={() => navigate('/')}
          className="text-sm font-semibold text-slate-200 hover:text-white"
          data-testid="public-watch-home-link"
        >
          MyInvestFolio
        </button>
      </header>

      <main className="flex-1 flex items-start justify-center px-4 sm:px-6 py-10">
        <div className="w-full max-w-3xl">
          {loading && (
            <div className="flex items-center justify-center py-24 text-slate-400" data-testid="public-watch-loading">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading video...
            </div>
          )}

          {!loading && notFound && (
            <div className="text-center py-24" data-testid="public-watch-not-found">
              <h1 className="text-2xl font-bold mb-3">Video not found</h1>
              <p className="text-sm text-slate-400 mb-6">This link may be invalid or the video may have been removed.</p>
              <Button onClick={() => navigate('/')} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                Go to MyInvestFolio
              </Button>
            </div>
          )}

          {!loading && video && (
            <>
              <div className="mb-5">
                <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="public-watch-title">{video.title}</h1>
                {video.subtitle && (
                  <p className="text-sm text-slate-400">{video.subtitle}</p>
                )}
              </div>

              <div className="rounded-lg overflow-hidden bg-black shadow-lg border border-slate-800 aspect-video">
                {(video.video_url_webm || video.video_url_mp4 || video.video_url) ? (
                  <video
                    key={`${video.video_url_webm || ''}|${video.video_url_mp4 || ''}|${video.video_url || ''}`}
                    className="w-full h-full"
                    controls autoPlay
                    data-testid="public-watch-video"
                  >
                    {video.video_url_webm && <source src={`${BACKEND_URL}${video.video_url_webm}`} type="video/webm" />}
                    {video.video_url_mp4 && <source src={`${BACKEND_URL}${video.video_url_mp4}`} type="video/mp4" />}
                    {video.video_url && !video.video_url_webm && !video.video_url_mp4 && (
                      <source src={`${BACKEND_URL}${video.video_url}`} />
                    )}
                  </video>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-500 text-sm">
                    Video coming soon.
                  </div>
                )}
              </div>

              {video.description && (
                <p className="text-sm text-slate-300 leading-relaxed mt-5" data-testid="public-watch-description">
                  {video.description}
                </p>
              )}

              <div className="mt-10 pt-8 border-t border-slate-800 text-center">
                <p className="text-sm text-slate-400 mb-3">MyInvestFolio &mdash; Continuity System and Portfolio Clarity Infrastructure</p>
                <Button
                  onClick={() => navigate('/?invite=1')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="public-watch-cta"
                >
                  Request an invitation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
